import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd, articleJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { SOURCE_ALKOHOLLAGEN, SOURCE_FOHM_VAGLEDNING, LAST_REVIEWED } from "@/lib/official-sources";

const faqs = [
  { q: "Vad reglerar alkohollagen 3 kap?", a: "3 kap. innehåller allmänna bestämmelser om försäljning av alkoholdrycker, åldersgränser och förbud mot servering till märkbart påverkade personer." },
  { q: "Vilken är åldersgränsen enligt 3 kap?", a: "20 år för köp på Systembolaget, 18 år för servering på restaurang och för folköl i livsmedelsbutik." },
  { q: "Vad innebär märkbart påverkad?", a: "Servering ska vägras gäst som är märkbart påverkad av alkohol eller annat berusningsmedel – detta är en av de vanligaste frågorna i kunskapsprovet." },
];

export const Route = createFileRoute("/alkohollagen-kapitel-3")({
  component: () => (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Guider", to: "/serveringstillstand-och-kunskapsprov" },
        { label: "Alkohollagen kap 3" },
      ]}
      title="Alkohollagen 3 kap – allmänna bestämmelser om försäljning"
      intro="3 kap. i alkohollagen (2010:1622) är ryggraden i kunskapsprovet. Här går vi igenom åldersgränser, förbud mot servering till påverkade och vad du behöver kunna inför provet."
      lastReviewed={LAST_REVIEWED}
      sources={[SOURCE_ALKOHOLLAGEN, SOURCE_FOHM_VAGLEDNING]}
      sections={[
        { h2: "Vad står i 3 kap?", body: "Kapitlet reglerar grundläggande regler för försäljning och servering av alkoholdrycker. Det omfattar bland annat åldersgränser, förbud mot servering till märkbart påverkade personer och kravet på återhållsam servering." },
        { h2: "Åldersgränser (3 kap. 7–8 §§)", body: "20 år för köp på Systembolaget. 18 år för servering på restaurang och för köp av folköl i livsmedelsbutik. Den som säljer eller serverar är skyldig att förvissa sig om åldern." },
        { h2: "Förbud mot servering till påverkade (3 kap. 8 §)", body: "Alkoholdrycker får inte serveras till någon som är märkbart påverkad av alkohol eller annat berusningsmedel. Servering ska också vägras om det finns risk att gästen stör ordningen eller utsätter sig själv eller andra för fara." },
        { h2: "Vanliga provfrågor från 3 kap", body: "Frågor handlar ofta om åldersgränser, krav på legitimation, definitionen av märkbart påverkad och vem som är ansvarig om en minderårig får alkohol serverad. Öva på dessa i modulen Ålderskontroll." },
      ]}
      faqs={faqs}
      related={{
        title: "Relaterade sidor",
        links: [
          { to: "/serveringstillstand-och-kunskapsprov", label: "Hub – alla guider" },
          { to: "/alkohollagen-kapitel-8", label: "Alkohollagen kapitel 8" },
          { to: "/alkohollagen-fragor", label: "Frågor om alkohollagen" },
          { to: "/kunskapsprov-alkohollagen", label: "Kunskapsprov i alkohollagen" },
          { to: "/serveringstillstand", label: "Serveringstillstånd" },
        ],
      }}
    />
  ),
  head: () => ({
    meta: [
      { title: "Alkohollagen 3 kap – allmänna bestämmelser | Alkoholprovet" },
      { name: "description", content: "Sammanfattning av alkohollagen 3 kap: åldersgränser, märkbart påverkad och vanliga frågor i kunskapsprovet för serveringstillstånd." },
      { property: "og:title", content: "Alkohollagen 3 kap – allmänna bestämmelser" },
      { property: "og:description", content: "Åldersgränser, servering till påverkade och de viktigaste paragraferna i 3 kap. för kunskapsprovet." },
      { property: "og:url", content: "https://alkoholprovet.se/alkohollagen-kapitel-3" },
    ],
    links: [{ rel: "canonical", href: "https://alkoholprovet.se/alkohollagen-kapitel-3" }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Guider", url: "https://alkoholprovet.se/serveringstillstand-och-kunskapsprov" },
        { label: "Alkohollagen kap 3", url: "https://alkoholprovet.se/alkohollagen-kapitel-3" },
      ]),
      articleJsonLd({
        url: "https://alkoholprovet.se/alkohollagen-kapitel-3",
        headline: "Alkohollagen 3 kap – allmänna bestämmelser | Alkoholprovet",
        description: "Sammanfattning av alkohollagen 3 kap: åldersgränser, märkbart påverkad och vanliga frågor i kunskapsprovet för serveringstillstånd.",
        datePublished: "2026-01-15",
        dateModified: "2026-06-10",
      }),
    ],
  }),
});
